// src/controller-profile.ts
var slots = /* @__PURE__ */ new Set(["joyL", "joyR", "A", "B", "X", "Y", "L1", "L2", "R1", "R2", "start", "select", "dpadU", "dpadD", "dpadL", "dpadR"]);
var object = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var bounded = (v, min, max) => typeof v === "number" && Number.isFinite(v) && v >= min && v <= max;
var colour = (v) => v === void 0 || typeof v === "string" && /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(v);
function validGraphic(value) {
  if (!object(value) || typeof value.png !== "string" || value.png.length > 12288 || !/^[a-z0-9+/]+={0,2}$/i.test(value.png) || value.png.length % 4 !== 0) return false;
  try {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    const bytes = [];
    for (let i = 0; i < 32; i += 4) {
      const bits = alphabet.indexOf(value.png[i]) << 18 | alphabet.indexOf(value.png[i + 1]) << 12 | alphabet.indexOf(value.png[i + 2]) << 6 | alphabet.indexOf(value.png[i + 3]);
      bytes.push(bits >>> 16 & 255, bits >>> 8 & 255, bits & 255);
    }
    const header = String.fromCharCode(...bytes);
    if (header.length < 24 || ![137, 80, 78, 71, 13, 10, 26, 10].every((b, i) => header.charCodeAt(i) === b) || header.slice(12, 16) !== "IHDR") return false;
    const word = (i) => header.charCodeAt(i) * 16777216 + (header.charCodeAt(i + 1) << 16) + (header.charCodeAt(i + 2) << 8) + header.charCodeAt(i + 3);
    return bounded(word(16), 1, 256) && bounded(word(20), 1, 256);
  } catch {
    return false;
  }
}
function validPlacement(value, stick) {
  if (!object(value) || !bounded(value.x, 0, 1) || !bounded(value.y, 0, 1) || !bounded(value.size, stick ? 96 : 48, 192) || value.shape !== void 0 && (typeof value.shape !== "string" || !["circle", "rounded"].includes(value.shape))) return false;
  if (value.zone !== void 0) {
    const z = value.zone;
    if (!stick || !object(z) || !bounded(z.x, 0, 1) || !bounded(z.y, 0, 1) || !bounded(z.width, 0.1, 1) || !bounded(z.height, 0.1, 1) || z.x + z.width > 1.000001 || z.y + z.height > 1.000001 || value.x < z.x || value.x > z.x + z.width || value.y < z.y || value.y > z.y + z.height) return false;
  }
  return true;
}
function isControllerSpec(value) {
  if (!object(value) || value.version !== 1 || typeof value.name !== "string" || value.name.length > 128 || !object(value.slots)) return false;
  try {
    const json = JSON.stringify(value);
    let bytes = 0;
    for (const character of json) {
      const code = character.codePointAt(0);
      bytes += code < 128 ? 1 : code < 2048 ? 2 : code < 65536 ? 3 : 4;
    }
    if (bytes > 49152) return false;
  } catch {
    return false;
  }
  const keys = Object.keys(value.slots);
  if (keys.length > 16) return false;
  let artwork = 0;
  for (const key of keys) {
    const slot = value.slots[key];
    if (!slots.has(key) || !object(slot) || slot.mode !== void 0 && (typeof slot.mode !== "string" || !["heading", "xy", "tilt"].includes(slot.mode)) || !colour(slot.colour) || slot.label !== void 0 && (typeof slot.label !== "string" || slot.label.length > 48) || slot.icon !== void 0 && (typeof slot.icon !== "string" || slot.icon.length > 64) || slot.tapAction !== void 0 && (typeof slot.tapAction !== "string" || !slots.has(slot.tapAction))) return false;
    if (slot.graphic !== void 0) {
      if (!validGraphic(slot.graphic)) return false;
      artwork += slot.graphic.png.length;
    }
  }
  if (artwork > 32768) return false;
  if (value.theme !== void 0 && (!object(value.theme) || !colour(value.theme.background) || !colour(value.theme.accent))) return false;
  if (value.layout !== void 0) {
    const l = value.layout;
    if (!object(l) || l.version !== 1 || l.portrait === void 0 && l.landscape === void 0) return false;
    for (const orientation of ["portrait", "landscape"]) {
      const map = l[orientation];
      if (map === void 0) continue;
      if (!object(map) || Object.keys(map).length !== keys.length || keys.some((key) => !validPlacement(map[key], key.startsWith("joy")))) return false;
    }
  }
  return true;
}
function resolvePlacement(placement, width, height) {
  const size = Math.max(0, Math.min(placement.size, width - 8, height - 8));
  const x = Math.max(size / 2 + 4, Math.min(width - size / 2 - 4, placement.x * width));
  const y = Math.max(size / 2 + 4, Math.min(height - size / 2 - 4, placement.y * height));
  const z = placement.zone;
  return { x, y, size, shape: placement.shape ?? "circle", zone: z ? { x: z.x * width, y: z.y * height, width: z.width * width, height: z.height * height } : { x: x - size / 2, y: y - size / 2, width: size, height: size } };
}
function resolveControllerLayout(spec, width, height) {
  if (width < 200 || height < 160) return null;
  const map = width > height ? spec.layout?.landscape : spec.layout?.portrait;
  if (!map) return null;
  const result = {};
  for (const key of Object.keys(spec.slots)) result[key] = resolvePlacement(map[key], width, height);
  const entries = Object.entries(result);
  const overlap = (a, b) => Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) > 0.01 && Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y) > 0.01;
  for (const [key, p] of entries) {
    if (key.startsWith("joy") && (p.x < p.zone.x || p.x > p.zone.x + p.zone.width || p.y < p.zone.y || p.y > p.zone.y + p.zone.height)) return null;
  }
  for (let i = 0; i < entries.length; i++) for (let j = i + 1; j < entries.length; j++) {
    const [a, ap] = entries[i], [b, bp] = entries[j];
    if (a.startsWith("joy") === b.startsWith("joy") && overlap(ap.zone, bp.zone)) return null;
  }
  return result;
}

export {
  validGraphic,
  validPlacement,
  isControllerSpec,
  resolvePlacement,
  resolveControllerLayout
};
//# sourceMappingURL=chunk-KA7HVMDJ.js.map